import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { documentsAndMessagesKeys } from "../api/documentsAndMessagesKeys";
import type { DocumentsAndMessagesResponse } from "../api/types";
import { useDocumentViewerStore } from "../documentViewerStore";
import { subscribeCoord } from "./channel";

/**
 * Opener-side listener for the viewer's `doc-moved` message (OR-23048). When
 * the analyst moves the currently-viewed document to a different approval
 * number, the viewer clears itself locally and broadcasts `doc-moved`; this
 * hook reacts by dropping the (now stale) focused-document selection and
 * removing the moved document from the docs & messages list.
 *
 * The removal is applied directly to the cache (not just an invalidate +
 * refetch) because the BFF's own propagation of the move can lag behind the
 * mutation's response — an immediate refetch would otherwise race that and
 * return the document as still present. `invalidateQueries` still runs after,
 * so the list eventually resyncs (message/unread counts, etc.) once the
 * backend has settled.
 * @param {string} approvalId - The deal / approval identifier this Review page is showing.
 */
export function useDocumentMoveListener(approvalId: string): void {
  const queryClient = useQueryClient();
  const clearFocusedDocument = useDocumentViewerStore((state) => state.clearFocusedDocument);

  useEffect(() => {
    const unsubscribe = subscribeCoord((message) => {
      if (message.type === "doc-moved" && message.appId === approvalId) {
        clearFocusedDocument();
        const queryKey = documentsAndMessagesKeys.detail({ approvalId });
        queryClient.setQueryData<DocumentsAndMessagesResponse>(queryKey, (previous) => {
          if (!previous) {
            return previous;
          }
          const documents = previous.data.documents.filter((document) => document.docId !== message.docId);
          if (documents.length === previous.data.documents.length) {
            return previous;
          }
          const documentCount = Math.max(0, previous.data.documentCount - 1);
          return { ...previous, data: { ...previous.data, documents, documentCount } };
        });
        void queryClient.invalidateQueries({ queryKey });
      }
    });
    return unsubscribe;
  }, [approvalId, clearFocusedDocument, queryClient]);
}
